import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import Navbar from '../components/Navbar'
import { contentPermalink } from '../lib/contentPermalink'

// ── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

const REASON_LABELS = {
  copyright:      'Copyright',
  inappropriate:  'Inappropriate',
  spam:           'Spam',
  harassment:     'Harassment',
  misinformation: 'Misinformation',
  other:          'Other',
}

const STATUS_COLORS = {
  pending:   '#f59e0b',
  resolved:  '#22c55e',
  dismissed: '#6b7280',
}

// ── Cron failures panel (used inside OverviewTab) ───────────────────────────
// Surfaces unresolved rows from public.cron_failures (migration 185). Today
// the only cron writer is hard_delete_pending_accounts_daily; the table is a
// shared abstraction for any future cron failure surface (e.g. the upcoming
// CSAM freeze RPC redesign).
function CronFailuresPanel({ headers }) {
  const [failures, setFailures] = useState([])
  const [loading, setLoading]   = useState(true)

  useEffect(() => {
    let active = true
    axios.get('/api/admin/cron-failures', { headers })
      .then(r => { if (active) setFailures(r.data?.failures ?? []) })
      .catch(err => console.error('[AdminPage] Failed to fetch cron failures:', err))
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [headers])

  return (
    <div className="admin-section">
      <h3 className="admin-section__title">Cron failures</h3>
      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : failures.length === 0 ? (
        <p className="admin-empty">No unresolved cron failures.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Job</th>
              <th>Target</th>
              <th>Error</th>
              <th>SQLSTATE</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {failures.map(f => (
              <tr key={f.id}>
                <td>{f.jobname}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{f.target_id ?? '—'}</td>
                <td style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.error_message}</td>
                <td style={{ fontFamily: 'monospace' }}>{f.sqlstate ?? '—'}</td>
                <td>{timeAgo(f.occurred_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── Online users panel (used inside OverviewTab) ────────────────────────────

function OnlineUsersPanel({ headers }) {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    function fetchOnline() {
      axios.get('/api/admin/online-users', { headers })
        .then(r => { if (active) setUsers(r.data) })
        .catch(err => console.error('[AdminPage] Failed to fetch online users:', err))
        .finally(() => { if (active) setLoading(false) })
    }
    fetchOnline()
    const interval = setInterval(fetchOnline, 60_000)
    return () => { active = false; clearInterval(interval) }
  }, [headers])

  return (
    <div className="admin-section">
      <h3 className="admin-section__title">Online Users ({loading ? '…' : users.length})</h3>
      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : users.length === 0 ? (
        <p className="admin-empty">No users online right now.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {users.map(u => (
            <Link
              key={u.id}
              to={`/user/${u.username}`}
              className="admin-link"
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: 'var(--bg)', textDecoration: 'none', fontSize: '0.85rem' }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: u.avatarUrl ? undefined : (u.avatarGrad ?? 'linear-gradient(135deg,#7C3AED,#EC4899)'),
                color: '#fff', fontSize: '0.7rem', fontWeight: 700,
                overflow: 'hidden',
              }}>
                {u.avatarUrl
                  ? <img src={u.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : (u.displayName?.[0] ?? '?').toUpperCase()}
              </div>
              <span>
                <span style={{ color: '#22c55e', marginRight: 4 }}>●</span>
                @{u.username}
                {u.hiddenStatus && (
                  <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--text-faint)', fontStyle: 'italic' }}>(hidden)</span>
                )}
              </span>
              <span style={{ color: 'var(--text-faint)', fontSize: '0.75rem', marginLeft: 'auto' }}>
                {timeAgo(u.lastSeenAt)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({ headers }) {
  const [stats, setStats]   = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axios.get('/api/admin/stats', { headers })
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers])

  if (loading) return <div className="admin-loading">Loading…</div>
  if (!stats)  return <div className="admin-empty">Failed to load stats.</div>

  return (
    <div className="admin-overview">
      <div className="admin-stat-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-card__value">{stats.users.toLocaleString()}</div>
          <div className="admin-stat-card__label">Total users</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__value">{stats.stories.toLocaleString()}</div>
          <div className="admin-stat-card__label">Total stories</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__value">{stats.reportsPending}</div>
          <div className="admin-stat-card__label">Pending reports</div>
          <div className="admin-stat-card__sub">{stats.reportsTotal} total</div>
        </div>
      </div>

      <OnlineUsersPanel headers={headers} />

      <CronFailuresPanel headers={headers} />

      <div className="admin-section">
        <h3 className="admin-section__title">Recent sign-ups</h3>
        {stats.recentUsers.length === 0
          ? <p className="admin-empty">No users yet.</p>
          : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Display name</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentUsers.map(u => (
                  <tr key={u.username}>
                    <td><Link to={`/user/${u.username}`} className="admin-link">@{u.username}</Link></td>
                    <td>{u.display_name || '—'}</td>
                    <td>{timeAgo(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>
    </div>
  )
}

// ── Message viewer modal ───────────────────────────────────────────────────────

function MessageViewerModal({ messageId, headers, onClose }) {
  const toast = useToast()
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [deletingMsg, setDeletingMsg] = useState(null)

  useEffect(() => {
    axios.get(`/api/admin/messages/${messageId}`, { headers })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load messages'))
      .finally(() => setLoading(false))
  }, [messageId])

  useEffect(() => {
    if (!data) return
    const el = document.getElementById(`admin-msg-${data.reportedMessageId}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [data])

  async function handleDeleteMsg(msgId) {
    if (!window.confirm('Delete this message? This cannot be undone.')) return
    setDeletingMsg(msgId)
    try {
      await axios.delete(`/api/admin/messages/${msgId}`, { headers })
      setData(prev => ({ ...prev, messages: prev.messages.filter(m => m.id !== msgId) }))
    } catch (e) {
      toast(e.response?.data?.error || 'Failed to delete message', 'error')
    } finally {
      setDeletingMsg(null)
    }
  }

  const profileMap = data ? Object.fromEntries(data.participants.map(p => [p.id, p])) : {}

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal admin-modal--messages" onClick={e => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3 className="admin-modal__title">
            Message conversation
            {data && (
              <span className="admin-modal__subtitle">
                {' '}— {data.participants.map(p => (
                  <Link key={p.id} to={`/user/${p.username}`} className="admin-link"> {p.display_name || p.username}</Link>
                )).reduce((a, b) => [a, ' & ', b])}
              </span>
            )}
          </h3>
          <button className="admin-modal__close" aria-label="Close" onClick={onClose}>✕</button>
        </div>

        <div className="admin-msg-list">
          {loading && <div className="admin-loading">Loading conversation…</div>}
          {error && <div className="admin-empty">{error}</div>}
          {data && data.messages.map(m => {
            const sender = profileMap[m.senderId]
            const isReported = m.id === data.reportedMessageId
            return (
              <div
                key={m.id}
                id={`admin-msg-${m.id}`}
                className={`admin-msg${isReported ? ' admin-msg--reported' : ''}`}
              >
                <div className="admin-msg__sender">
                  <Link to={`/user/${sender?.username ?? 'unknown'}`} className="admin-link">
                    @{sender?.username ?? 'unknown'}
                  </Link>
                  <span className="admin-msg__time">
                    {new Date(m.createdAt).toLocaleString()}
                    {m.edited && ' (edited)'}
                  </span>
                  {isReported && <span className="admin-badge" style={{ background: '#ef4444', marginLeft: 8 }}>Reported</span>}
                  <button
                    className="admin-msg__delete"
                    disabled={deletingMsg === m.id}
                    onClick={() => handleDeleteMsg(m.id)}
                    title="Delete this message"
                  >{deletingMsg === m.id ? '…' : '🗑'}</button>
                </div>
                <div className="admin-msg__content">{m.content}</div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── Content body viewer (admin/mod review of reported/flagged content) ─────────
// Fetches GET /api/admin/content/:type/:id and renders the full body regardless
// of visibility state. Mirrors MessageViewerModal — one shared modal whose body
// render switches on `type`. The six Tier-2 report types route here.
const VIEWABLE_CONTENT_TYPES = ['story', 'chapter', 'comment', 'quiz', 'group_post', 'announcement']

function VisibilityBadges({ visibility, parent }) {
  const badges = []
  if (visibility) {
    if (visibility.hidden)              badges.push(['Hidden', '#ef4444'])
    if (visibility.deleted)             badges.push(['Deleted', '#6b7280'])
    if (visibility.published === false) badges.push(['Unpublished', '#f59e0b'])
    if (visibility.isDraft)             badges.push(['Draft', '#f59e0b'])
  }
  // Chapter has no own hidden/deleted — surface the parent story's state instead.
  if (parent?.hidden)              badges.push(['Parent hidden', '#ef4444'])
  if (parent?.deleted)             badges.push(['Parent deleted', '#6b7280'])
  if (parent && parent.published === false) badges.push(['Parent unpublished', '#f59e0b'])
  if (!badges.length) badges.push(['Visible', '#22c55e'])
  return (
    <span>
      {badges.map(([label, bg]) => (
        <span key={label} className="admin-badge" style={{ background: bg, marginRight: 4 }}>{label}</span>
      ))}
    </span>
  )
}

function ContentBody({ type, data }) {
  const b = data.body ?? {}
  const metaStyle = { fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }
  const textStyle = { whiteSpace: 'pre-wrap', marginTop: 8 }
  switch (type) {
    case 'story':
      return (
        <div>
          <h4 style={{ margin: '0 0 8px' }}>{b.title || '(untitled)'}</h4>
          {b.fandom && <div style={metaStyle}>Fandom: {b.fandom}</div>}
          {b.rating && <div style={metaStyle}>Rating: {b.rating}</div>}
          {b.status && <div style={metaStyle}>Status: {b.status}</div>}
          {Array.isArray(b.tags) && b.tags.length > 0 && <div style={metaStyle}>Tags: {b.tags.join(', ')}</div>}
          <div style={textStyle}>{b.description || '(no description)'}</div>
          <div style={{ ...metaStyle, marginTop: 10, fontStyle: 'italic' }}>
            A story&apos;s narrative lives in its chapters — review individual chapters via their own reports.
          </div>
        </div>
      )
    case 'chapter':
      return (
        <div>
          {data.parent && <div style={metaStyle}>In story: {data.parent.title || data.parent.id} · chapter {b.number}</div>}
          <h4 style={{ margin: '0 0 8px' }}>{b.title || `Chapter ${b.number}`}</h4>
          <div style={textStyle}>{b.content || '(empty)'}</div>
        </div>
      )
    case 'comment':
      return (
        <div>
          {data.parent && <div style={metaStyle}>On {data.parent.surface}: {data.parent.id}</div>}
          <div style={textStyle}>{b.content || '(empty)'}</div>
        </div>
      )
    case 'quiz':
      return (
        <div>
          <h4 style={{ margin: '0 0 8px' }}>{b.title || '(untitled)'}</h4>
          {b.description && <div style={textStyle}>{b.description}</div>}
          {Array.isArray(b.questions) && b.questions.length > 0 && (
            <ol style={{ marginTop: 12, paddingLeft: 20 }}>
              {b.questions.map(q => <li key={q.number} style={{ marginBottom: 4 }}>{q.text}</li>)}
            </ol>
          )}
        </div>
      )
    case 'group_post':
      return (
        <div>
          {data.parent && <div style={metaStyle}>In group: {data.parent.id}</div>}
          <div style={textStyle}>{b.content || '(empty)'}</div>
        </div>
      )
    case 'announcement':
      return <div style={textStyle}>{b.content || '(empty)'}</div>
    default:
      return null
  }
}

function ContentViewerModal({ type, id, headers, onClose }) {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  useEffect(() => {
    axios.get(`/api/admin/content/${type}/${id}`, { headers })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load content'))
      .finally(() => setLoading(false))
  }, [type, id])

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal" onClick={e => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3 className="admin-modal__title">
            {type.replace('_', ' ')} content
            {data?.author && (
              <span className="admin-modal__subtitle">
                {' '}— <Link to={`/user/${data.author.username}`} className="admin-link">@{data.author.username}</Link>
              </span>
            )}
          </h3>
          <button className="admin-modal__close" aria-label="Close" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: 16, maxHeight: '60vh', overflowY: 'auto' }}>
          {loading && <div className="admin-loading">Loading…</div>}
          {error && <div className="admin-empty">{error}</div>}
          {data && !loading && !error && (
            data.found === false
              ? <div className="admin-empty">This content no longer exists.</div>
              : (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <VisibilityBadges visibility={data.visibility} parent={data.parent} />
                    {data.createdAt && <span className="admin-msg__time" style={{ marginLeft: 8 }}>{timeAgo(data.createdAt)}</span>}
                  </div>
                  <ContentBody type={type} data={data} />
                </>
              )
          )}
        </div>
      </div>
    </div>
  )
}

// ── Reports tab ───────────────────────────────────────────────────────────────

function ReportsTab({ headers, userRole }) {
  const { user, profile } = useAuth()
  const toast = useToast()
  const [reports, setReports]         = useState([])
  const [statusFilter, setStatusFilter] = useState('pending')
  const [loading, setLoading]         = useState(true)
  const [actioning, setActioning]     = useState(null)
  const [deleting, setDeleting]       = useState(null)
  const [viewingMessage, setViewingMessage] = useState(null)
  const [viewingContent, setViewingContent] = useState(null)
  const [expandedReasons, setExpandedReasons] = useState(() => new Set())

  function toggleReason(id) {
    setExpandedReasons(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const load = useCallback((status) => {
    setLoading(true)
    axios.get(`/api/admin/reports?status=${status}`, { headers })
      .then(r => setReports(r.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers])

  useEffect(() => { load(statusFilter) }, [statusFilter, load])

  async function updateStatus(id, status) {
    setActioning(id)
    try {
      await axios.patch(`/api/admin/reports/${id}`, { status }, { headers })
      if (statusFilter === 'all') {
        setReports(prev => prev.map(r => r.id === id ? { ...r, status } : r))
      } else {
        setReports(prev => prev.filter(r => r.id !== id))
      }
    } catch (e) {
      console.error(e)
    } finally {
      setActioning(null)
    }
  }

  async function deleteReport(id) {
    if (!window.confirm('Permanently delete this report? This cannot be undone.')) return
    setDeleting(id)
    try {
      await axios.delete(`/api/admin/reports/${id}`, { headers })
      setReports(prev => prev.filter(r => r.id !== id))
    } catch (e) {
      console.error(e)
    } finally {
      setDeleting(null)
    }
  }

  async function assignReport(id, assignTo) {
    try {
      await axios.patch(`/api/admin/reports/${id}/assign`, { assignTo }, { headers })
      setReports(prev => prev.map(r => r.id === id ? { ...r, assigned_to: assignTo, assignee_username: assignTo ? (profile?.username ?? 'me') : null } : r))
    } catch (e) {
      console.error(e)
    }
  }

  const FILTER_OPTIONS = ['pending', 'resolved', 'dismissed', 'all']

  return (
    <div className="admin-reports">
      <div className="admin-filter-row">
        {FILTER_OPTIONS.map(s => (
          <button
            key={s}
            className={`admin-filter-btn${statusFilter === s ? ' admin-filter-btn--active' : ''}`}
            onClick={() => setStatusFilter(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading
        ? <div className="admin-loading">Loading…</div>
        : reports.length === 0
          ? <div className="admin-empty">No {statusFilter === 'all' ? '' : statusFilter} reports.</div>
          : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Content</th>
                    <th>Reason</th>
                    <th>Reporter</th>
                    <th>Offender</th>
                    <th>Assigned</th>
                    <th>Details</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map(r => (
                    <tr key={r.id}>
                      <td style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                        {r.content_type} #{r.content_id}
                      </td>
                      <td>{REASON_LABELS[r.reason] ?? r.reason}</td>
                      <td>
                        {r.reporter_username
                          ? <Link to={`/user/${r.reporter_username}`} className="admin-link">@{r.reporter_username}</Link>
                          : '—'}
                      </td>
                      <td>
                        {r.offender_username
                          ? <Link to={`/user/${r.offender_username}`} className="admin-link">@{r.offender_username}</Link>
                          : '—'}
                      </td>
                      <td>
                        {r.assignee_username
                          ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              @{r.assignee_username}
                              <button className="admin-action-btn" style={{ fontSize: '0.7rem', padding: '2px 4px' }} onClick={() => assignReport(r.id, null)}>×</button>
                            </span>
                          : <button className="admin-action-btn" style={{ fontSize: '0.75rem' }} onClick={() => assignReport(r.id, user?.id)}>Assign to me</button>
                        }
                      </td>
                      {r.details
                        ? <td
                            className={`admin-table__details admin-table__details--clickable${expandedReasons.has(r.id) ? ' admin-table__details--expanded' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-expanded={expandedReasons.has(r.id)}
                            title={expandedReasons.has(r.id) ? 'Click to collapse' : 'Click to read full reason'}
                            onClick={() => toggleReason(r.id)}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleReason(r.id) } }}
                          >{r.details}</td>
                        : <td className="admin-table__details">—</td>}
                      <td>
                        <span className="admin-badge" style={{ background: STATUS_COLORS[r.status] }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(r.created_at)}</td>
                      <td>
                        <div className="admin-action-row">
                          {r.status !== 'resolved' && (
                            <button
                              className="admin-action-btn admin-action-btn--green"
                              disabled={actioning === r.id}
                              onClick={() => updateStatus(r.id, 'resolved')}
                            >Resolve</button>
                          )}
                          {r.status !== 'dismissed' && (
                            <button
                              className="admin-action-btn admin-action-btn--gray"
                              disabled={actioning === r.id}
                              onClick={() => updateStatus(r.id, 'dismissed')}
                            >Dismiss</button>
                          )}
                          {r.content_url && r.content_url.startsWith('__message__') ? (
                            <button
                              className="admin-action-btn admin-action-btn--blue"
                              onClick={() => setViewingMessage(r.content_url.replace('__message__', ''))}
                            >View message</button>
                          ) : VIEWABLE_CONTENT_TYPES.includes(r.content_type) ? (
                            <button
                              className="admin-action-btn admin-action-btn--blue"
                              onClick={() => setViewingContent({ type: r.content_type, id: r.content_id })}
                            >View {r.content_type.replace('_', ' ')}</button>
                          ) : r.content_url && (
                            <Link to={r.content_url} className="admin-action-btn admin-action-btn--blue" target="_blank">
                              View {r.content_type.replace('_', ' ')}
                            </Link>
                          )}
                          {(r.status === 'resolved' || r.status === 'dismissed') && (
                            <button
                              className="admin-action-btn admin-action-btn--orange"
                              disabled={actioning === r.id}
                              onClick={() => updateStatus(r.id, 'pending')}
                            >Unresolve</button>
                          )}
                          {userRole === 'admin' && (r.status === 'resolved' || r.status === 'dismissed') && (
                            <button
                              className="admin-action-btn admin-action-btn--red"
                              disabled={deleting === r.id}
                              onClick={() => deleteReport(r.id)}
                            >{deleting === r.id ? '…' : 'Delete'}</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      }
      {viewingMessage && (
        <MessageViewerModal
          messageId={viewingMessage}
          headers={headers}
          onClose={() => setViewingMessage(null)}
        />
      )}
      {viewingContent && (
        <ContentViewerModal
          type={viewingContent.type}
          id={viewingContent.id}
          headers={headers}
          onClose={() => setViewingContent(null)}
        />
      )}
    </div>
  )
}

// ── Users tab ─────────────────────────────────────────────────────────────────

function UsersTab({ headers, userRole }) {
  const toast = useToast()
  const [search, setSearch]             = useState('')
  const [roleFilter, setRoleFilter]     = useState('')
  const [users, setUsers]               = useState([])
  const [loading, setLoading]           = useState(false)
  const [selectedId, setSelectedId]     = useState(null)
  const [userDetail, setUserDetail]     = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [deleting, setDeleting]         = useState(false)
  const searchUsers = useCallback(async (q, role) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (q) params.set('search', q)
      if (role) params.set('role', role)
      const r = await axios.get(`/api/admin/users?${params}`, { headers })
      setUsers(r.data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [headers])

  useEffect(() => {
    const t = setTimeout(() => searchUsers(search, roleFilter), 300)
    return () => clearTimeout(t)
  }, [search, roleFilter, searchUsers])

  async function loadDetail(id) {
    setSelectedId(id)
    setUserDetail(null)
    setDetailLoading(true)
    try {
      const r = await axios.get(`/api/admin/users/${id}`, { headers })
      setUserDetail(r.data)
    } catch (e) {
      console.error(e)
    } finally {
      setDetailLoading(false)
    }
  }

  async function deleteStory(storyId) {
    if (!window.confirm('Delete this story? This cannot be undone.')) return
    await axios.delete(`/api/admin/stories/${storyId}`, { headers })
    setUserDetail(prev => ({ ...prev, stories: prev.stories.filter(s => s.id !== storyId) }))
  }

  async function deleteAnnouncement(announcementId) {
    if (!window.confirm('Delete this announcement? This cannot be undone.')) return
    await axios.delete(`/api/admin/announcements/${announcementId}`, { headers })
    setUserDetail(prev => ({
      ...prev,
      announcements: prev.announcements.map(a =>
        a.id === announcementId ? { ...a, deleted: true } : a
      ),
    }))
  }

  async function toggleRestricted(userId, currentlyRestricted) {
    const action = currentlyRestricted ? 'unrestrict' : 'restrict'
    if (!window.confirm(`${currentlyRestricted ? 'Unrestrict' : 'Restrict'} this user?`)) return
    try {
      await axios.patch(`/api/admin/users/${userId}/restricted`, { restricted: !currentlyRestricted }, { headers })
      setUserDetail(prev => ({ ...prev, profile: { ...prev.profile, restricted: !currentlyRestricted } }))
    } catch (e) {
      console.error(e)
    }
  }

  async function deleteUser(userId) {
    if (!window.confirm('Permanently delete this user and all their data? This cannot be undone.')) return
    setDeleting(true)
    try {
      await axios.delete(`/api/admin/users/${userId}`, { headers })
      setUsers(prev => prev.filter(u => u.id !== userId))
      setSelectedId(null)
      setUserDetail(null)
    } catch (e) {
      toast(e.response?.data?.error ?? 'Failed to delete user.', 'error')
    } finally {
      setDeleting(false)
    }
  }

  async function viewHash(imageUrl, userId, imageType) {
    try {
      const params = new URLSearchParams()
      if (imageUrl) params.set('url', imageUrl)
      if (userId) params.set('user_id', userId)
      if (imageType) params.set('image_type', imageType)
      const r = await axios.get(`/api/admin/image-hash?${params}`, { headers })
      window.prompt('Perceptual hash (pHash):', r.data.phash)
    } catch (e) {
      toast(e.response?.data?.error ?? 'Hash not found.', 'error')
    }
  }

  async function blockImageHash(imageUrl, userId, imageType) {
    if (!window.confirm('Add this image\'s hash to the CSAM blocklist? Future uploads of visually similar images will be automatically blocked.')) return
    try {
      await axios.post('/api/admin/csam-blocklist/from-image', { image_url: imageUrl, user_id: userId, image_type: imageType }, { headers })
      toast('Hash added to blocklist.', 'success')
    } catch (e) {
      toast(e.response?.data?.error ?? 'Failed to add hash.', 'error')
    }
  }

  async function requestSuspension(userId) {
    const reason = window.prompt('Reason for suspension request:')
    if (!reason?.trim()) return
    const duration = window.prompt('Duration (1h, 6h, 12h, 1d, 3d, 7d, 14d, 30d):')
    if (!duration?.trim()) return
    try {
      await axios.post('/api/admin/suspension-requests', { userId, reason, duration }, { headers })
      toast('Suspension request submitted for admin review.', 'success')
    } catch (e) {
      toast(e.response?.data?.error ?? 'Failed to submit request.', 'error')
    }
  }

  return (
    <div className="admin-users">
      <div className="admin-users__layout">

        {/* Left: user list */}
        <div className="admin-users__list">
          <input
            className="admin-search"
            placeholder="Search by username…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="admin-search"
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            style={{ marginTop: 6, cursor: 'pointer' }}
          >
            <option value="">All users</option>
            <option value="moderator">Moderators</option>
            <option value="admin">Admins</option>
            <option value="user">Regular users</option>
          </select>
          {loading
            ? <div className="admin-loading">Loading…</div>
            : users.length === 0
              ? <div className="admin-empty">No users found.</div>
              : users.map(u => (
                <button
                  key={u.id}
                  className={`admin-user-row${selectedId === u.id ? ' admin-user-row--active' : ''}`}
                  onClick={() => loadDetail(u.id)}
                >
                  <span className="admin-user-row__name">
                    {u.display_name || u.username}
                    {u.role === 'moderator' && (
                      <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: '#7C3AED', color: '#fff', fontSize: '0.65rem', fontWeight: 700, verticalAlign: 'middle' }}>MOD</span>
                    )}
                    {u.role === 'admin' && (
                      <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: '#EC4899', color: '#fff', fontSize: '0.65rem', fontWeight: 700, verticalAlign: 'middle' }}>ADMIN</span>
                    )}
                  </span>
                  <span className="admin-user-row__username">@{u.username}</span>
                  <span className="admin-user-row__meta">{timeAgo(u.created_at)}</span>
                </button>
              ))
          }
        </div>

        {/* Right: user detail */}
        <div className="admin-users__detail">
          {!selectedId && (
            <div className="admin-empty admin-empty--center">Select a user to view details.</div>
          )}
          {detailLoading && <div className="admin-loading">Loading…</div>}
          {userDetail && !detailLoading && (
            <>
              <div className="admin-user-header">
                <div>
                  <div className="admin-user-header__name">
                    {userDetail.profile.display_name || userDetail.profile.username}
                  </div>
                  <Link to={`/user/${userDetail.profile.username}`} className="admin-link" target="_blank">
                    @{userDetail.profile.username}
                  </Link>
                  <div className="admin-user-header__meta">
                    Joined {timeAgo(userDetail.profile.created_at)} · {userDetail.profile.followers} followers
                    {userDetail.profile.restricted && <span style={{ color: '#f59e0b', fontWeight: 600, marginLeft: 8 }}>RESTRICTED</span>}
                    {userDetail.profile.banned && <span style={{ color: '#dc2626', fontWeight: 600, marginLeft: 8 }}>BANNED</span>}
                    {userRole === 'admin' && (
                      <select
                        value={userDetail.profile.role ?? 'user'}
                        onChange={async (e) => {
                          const newRole = e.target.value
                          try {
                            await axios.patch(`/api/admin/users/${userDetail.profile.id}/role`, { role: newRole }, { headers })
                            setUserDetail(prev => ({ ...prev, profile: { ...prev.profile, role: newRole } }))
                            setUsers(prev => prev.map(u => u.id === userDetail.profile.id ? { ...u, role: newRole } : u))
                            toast(`Role updated to ${newRole}.`, 'success')
                          } catch (err) {
                            toast(err.response?.data?.error ?? 'Failed to update role.', 'error')
                          }
                        }}
                        style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '0.8rem' }}
                      >
                        <option value="user">User</option>
                        <option value="moderator">Moderator</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}
                  </div>
                  {userRole === 'admin' && (
                    <div className="admin-user-header__meta" style={{ marginTop: 4 }}>
                      User ID: <span style={{ fontFamily: 'monospace', userSelect: 'all' }}>{userDetail.profile.id}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className={`admin-action-btn${userDetail.profile.restricted ? '' : ' admin-action-btn--red'}`}
                    onClick={() => toggleRestricted(userDetail.profile.id, userDetail.profile.restricted)}
                  >
                    {userDetail.profile.restricted ? 'Unrestrict' : 'Restrict'}
                  </button>
                  {userRole === 'moderator' && (
                    <button
                      className="admin-action-btn admin-action-btn--orange"
                      onClick={() => requestSuspension(userDetail.profile.id)}
                    >
                      Request Suspension
                    </button>
                  )}
                  {userRole === 'admin' && (
                    <button
                      className="admin-action-btn admin-action-btn--red"
                      style={userDetail.profile.csam_frozen ? { background: '#059669' } : {}}
                      onClick={async () => {
                        const frozen = userDetail.profile.csam_frozen
                        const action = frozen ? 'Unfreeze' : 'CSAM Freeze'
                        if (!window.confirm(`${action} @${userDetail.profile.username}?${frozen ? '' : ' This preserves all content and blocks account deletion.'}`)) return
                        try {
                          if (frozen) {
                            await axios.delete(`/api/admin/csam-freeze/${userDetail.profile.id}`, { headers })
                          } else {
                            await axios.post(`/api/admin/csam-freeze/${userDetail.profile.id}`, {}, { headers })
                          }
                          setUserDetail(prev => ({ ...prev, profile: { ...prev.profile, csam_frozen: !frozen } }))
                          toast(`Account ${frozen ? 'unfrozen' : 'frozen'}.`, 'success')
                        } catch (e) {
                          toast(e.response?.data?.error ?? `${action} failed.`, 'error')
                        }
                      }}
                    >
                      {userDetail.profile.csam_frozen ? 'Unfreeze' : 'CSAM Freeze'}
                    </button>
                  )}
                  {userRole === 'admin' && (
                    <button
                      className="admin-action-btn admin-action-btn--red"
                      style={userDetail.profile.banned ? { background: '#059669' } : {}}
                      onClick={async () => {
                        const banned = userDetail.profile.banned
                        const action = banned ? 'Unban' : 'Ban'
                        const reason = window.prompt(`Reason for ${action.toLowerCase()} (10-1000 chars):`)
                        if (!reason?.trim() || reason.trim().length < 10) {
                          toast('Reason must be at least 10 characters.', 'error')
                          return
                        }
                        const confirmMsg = banned
                          ? `Unban @${userDetail.profile.username}? Their profile, stories, and quizzes will remain hidden — restore visibility manually if appropriate.`
                          : `Ban @${userDetail.profile.username}? This is permanent until manually reversed and will hide their profile, stories, and quizzes.`
                        if (!window.confirm(confirmMsg)) return
                        try {
                          if (banned) {
                            await axios.delete(`/api/admin/users/${userDetail.profile.id}/ban`, { headers, data: { reason } })
                          } else {
                            await axios.post(`/api/admin/users/${userDetail.profile.id}/ban`, { reason }, { headers })
                          }
                          setUserDetail(prev => ({ ...prev, profile: { ...prev.profile, banned: !banned } }))
                          toast(`Account ${banned ? 'unbanned' : 'banned'}.`, 'success')
                        } catch (e) {
                          toast(e.response?.data?.error ?? `${action} failed.`, 'error')
                        }
                      }}
                    >
                      {userDetail.profile.banned ? 'Unban' : 'Ban'}
                    </button>
                  )}
                  {userRole === 'admin' && (
                    <button
                      className="admin-action-btn admin-action-btn--red"
                      onClick={() => deleteUser(userDetail.profile.id)}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting…' : 'Delete user'}
                    </button>
                  )}
                </div>
              </div>

              {userRole === 'admin' && (userDetail.profile.avatar_url || userDetail.profile.banner_url) && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, fontSize: '0.8rem', flexWrap: 'wrap' }}>
                  {userDetail.profile.avatar_url && (
                    <>
                      <button className="admin-action-btn" onClick={() => viewHash(userDetail.profile.avatar_url, userDetail.profile.id, 'avatar')}>View Avatar Hash</button>
                      <button className="admin-action-btn admin-action-btn--red" onClick={() => blockImageHash(userDetail.profile.avatar_url, userDetail.profile.id, 'avatar')}>Block Avatar Hash</button>
                    </>
                  )}
                  {userDetail.profile.banner_url && (
                    <>
                      <button className="admin-action-btn" onClick={() => viewHash(userDetail.profile.banner_url, userDetail.profile.id, 'profile_banner')}>View Banner Hash</button>
                      <button className="admin-action-btn admin-action-btn--red" onClick={() => blockImageHash(userDetail.profile.banner_url, userDetail.profile.id, 'profile_banner')}>Block Banner Hash</button>
                    </>
                  )}
                </div>
              )}

              <div className="admin-section">
                <h3 className="admin-section__title">Coin Balance</h3>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.85rem' }}>
                  <div><strong>Purchased:</strong> {userDetail.coinBalance?.purchased_balance ?? 0}</div>
                  <div><strong>Earned:</strong> {userDetail.coinBalance?.earned_balance ?? 0}</div>
                  <div><strong>Held:</strong> {userDetail.coinBalance?.held_balance ?? 0}</div>
                  <div><strong>Total:</strong> {(userDetail.coinBalance?.purchased_balance ?? 0) + (userDetail.coinBalance?.earned_balance ?? 0) + (userDetail.coinBalance?.held_balance ?? 0)}</div>
                </div>
              </div>

              <div className="admin-section">
                <h3 className="admin-section__title">Stories ({userDetail.stories.length})</h3>
                {userDetail.stories.length === 0
                  ? <div className="admin-empty">No stories.</div>
                  : (
                    <div className="admin-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Status</th>
                            <th>Likes</th>
                            <th>Views</th>
                            <th>Date</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {userDetail.stories.map(s => (
                            <tr key={s.id}>
                              <td>
                                <Link to={`/story/${s.id}`} className="admin-link" target="_blank">
                                  {s.title}
                                </Link>
                              </td>
                              <td>{s.status}</td>
                              <td>{s.likes}</td>
                              <td>{s.views}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(s.created_at)}</td>
                              <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <Link to={`/story/${s.id}/edit`} className="admin-action-btn" target="_blank">Edit</Link>
                                {userRole === 'admin' && (
                                  <button className="admin-action-btn admin-action-btn--red" onClick={() => deleteStory(s.id)}>Delete</button>
                                )}
                                {userRole === 'admin' && (s.cover_url || s.banner_url) && (
                                  <button className="admin-action-btn admin-action-btn--red" style={{ fontSize: '0.7rem' }} onClick={() => blockImageHash(s.cover_url || s.banner_url, userDetail.profile.id, s.cover_url ? 'story_cover' : 'story_banner')}>Block Hash</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                }
              </div>

              <div className="admin-section">
                <h3 className="admin-section__title">Announcements ({userDetail.announcements?.length ?? 0})</h3>
                {userRole === 'admin' && (
                  <div className="admin-user-header__meta" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label htmlFor="announcement-limit-input">Daily posting limit:</label>
                    <input
                      id="announcement-limit-input"
                      type="number"
                      min={0}
                      max={10}
                      step={1}
                      value={userDetail.profile.announcement_limit ?? 1}
                      onChange={async (e) => {
                        const limit = Number(e.target.value)
                        if (!Number.isInteger(limit) || limit < 0 || limit > 10) return
                        try {
                          await axios.patch(`/api/admin/users/${userDetail.profile.id}/announcement-limit`, { limit }, { headers })
                          setUserDetail(prev => ({ ...prev, profile: { ...prev.profile, announcement_limit: limit } }))
                          toast(limit === 0 ? 'Announcement posting revoked.' : `Daily announcement limit set to ${limit}.`, 'success')
                        } catch (err) {
                          toast(err.response?.data?.error ?? 'Failed to update limit.', 'error')
                        }
                      }}
                      style={{ width: 60, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '0.8rem' }}
                    />
                    <span style={{ color: 'var(--text-muted)' }}>0 = posting privileges revoked</span>
                  </div>
                )}
                {(!userDetail.announcements || userDetail.announcements.length === 0)
                  ? <div className="admin-empty">No announcements.</div>
                  : (
                    <div className="admin-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Content</th>
                            <th>Date</th>
                            <th>Status</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {userDetail.announcements.map(a => (
                            <tr key={a.id} style={{ opacity: a.deleted ? 0.5 : 1 }}>
                              <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                {(a.content ?? '').slice(0, 100) || '—'}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(a.created_at)}</td>
                              <td>
                                {a.deleted
                                  ? <span className="admin-badge" style={{ background: '#6b7280' }}>Deleted</span>
                                  : <span className="admin-badge" style={{ background: '#22c55e' }}>Visible</span>}
                              </td>
                              <td>
                                {userRole === 'admin' && !a.deleted && (
                                  <button className="admin-action-btn admin-action-btn--red" onClick={() => deleteAnnouncement(a.id)}>Delete</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                }
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  )
}

// ── System Messages tab ──────────────────────────────────────────────────────

function SystemMessagesTab({ headers }) {
  const [messages, setMessages] = useState([])
  const [loading, setLoading]   = useState(true)
  const [title, setTitle]       = useState('')
  const [body, setBody]         = useState('')
  const [targetUser, setTargetUser] = useState('')
  const [sending, setSending]   = useState(false)
  const [error, setError]       = useState(null)
  const [success, setSuccess]   = useState(false)
  const [editing, setEditing]   = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [editBody, setEditBody]   = useState('')
  const [editSaving, setEditSaving] = useState(false)

  useEffect(() => {
    axios.get('/api/system-messages/admin/all', { headers })
      .then(r => setMessages(r.data))
      .catch(err => console.error('[AdminPage] Failed to fetch system messages:', err))
      .finally(() => setLoading(false))
  }, [headers])

  async function handleSend(e) {
    e.preventDefault()
    setSending(true)
    setError(null)
    setSuccess(false)
    try {
      const payload = { title: title.trim(), body: body.trim() }
      if (targetUser.trim()) payload.target_username = targetUser.trim()
      const { data } = await axios.post('/api/system-messages', payload, { headers })
      setMessages(prev => [data, ...prev])
      setTitle('')
      setBody('')
      setTargetUser('')
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err.response?.data?.error ?? 'Failed to send.')
    } finally {
      setSending(false)
    }
  }

  function startEdit(m) {
    setEditing(m.id)
    setEditTitle(m.title)
    setEditBody(m.body)
  }

  async function handleEditSave(id) {
    setEditSaving(true)
    try {
      const { data } = await axios.patch(`/api/system-messages/${id}`, { title: editTitle, body: editBody }, { headers })
      setMessages(prev => prev.map(m => m.id === id ? { ...m, ...data } : m))
      setEditing(null)
      window.dispatchEvent(new Event('system-messages-changed'))
    } catch (err) { console.error('[AdminPage] Failed to save system message edit:', err) }
    setEditSaving(false)
  }

  async function handleDelete(id) {
    try {
      await axios.delete(`/api/system-messages/${id}`, { headers })
      setMessages(prev => prev.filter(m => m.id !== id))
      window.dispatchEvent(new Event('system-messages-changed'))
    } catch (err) { console.error('[AdminPage] Failed to delete system message:', err) }
  }

  return (
    <div className="admin-section">
      <h2 className="admin-section__title">Send System Message</h2>
      <form onSubmit={handleSend} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        <input
          type="text"
          className="auth-input"
          placeholder="Title (required)"
          value={title}
          onChange={e => setTitle(e.target.value)}
          maxLength={200}
          required
        />
        <textarea
          className="auth-input"
          placeholder="Message body (required)"
          value={body}
          onChange={e => setBody(e.target.value)}
          maxLength={5000}
          rows={4}
          required
          style={{ resize: 'vertical' }}
        />
        <input
          type="text"
          className="auth-input"
          placeholder="Username (leave blank to broadcast to all)"
          value={targetUser}
          onChange={e => setTargetUser(e.target.value)}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-primary" type="submit" disabled={sending} style={{ fontSize: '0.875rem' }}>
            {sending ? 'Sending…' : targetUser.trim() ? 'Send to user' : 'Broadcast to all'}
          </button>
          {error && <span style={{ fontSize: '0.8rem', color: '#DC2626' }}>{error}</span>}
          {success && <span style={{ fontSize: '0.8rem', color: '#059669' }}>Sent!</span>}
        </div>
      </form>

      <h2 className="admin-section__title">Sent Messages</h2>
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading…</p>
      ) : messages.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No system messages yet.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Target</th>
                <th>Sent</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {messages.map(m => editing === m.id ? (
                <tr key={m.id}>
                  <td colSpan={4} style={{ padding: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <input
                        className="auth-input"
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        maxLength={200}
                        placeholder="Title"
                        style={{ fontSize: '0.85rem' }}
                      />
                      <textarea
                        className="auth-input"
                        value={editBody}
                        onChange={e => setEditBody(e.target.value)}
                        maxLength={5000}
                        rows={3}
                        placeholder="Body"
                        style={{ fontSize: '0.85rem', resize: 'vertical' }}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '6px 14px' }} onClick={() => handleEditSave(m.id)} disabled={editSaving || !editTitle.trim() || !editBody.trim()}>
                          {editSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '6px 14px' }} onClick={() => setEditing(null)} disabled={editSaving}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={m.id}>
                  <td style={{ fontWeight: 500 }}>{m.title}</td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {m.target_username ? `@${m.target_username}` : m.target_user ? m.target_user.slice(0, 8) + '…' : 'All users'}
                  </td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{timeAgo(m.created_at)}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button
                      className="admin-action-btn"
                      onClick={() => startEdit(m)}
                    >
                      Edit
                    </button>
                    <button
                      className="admin-action-btn admin-action-btn--red"
                      onClick={() => handleDelete(m.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Suspension Requests tab (admin only) ─────────────────────────────────────

function SuspensionRequestsTab({ headers }) {
  const toast = useToast()
  const [requests, setRequests] = useState([])
  const [loading, setLoading]   = useState(true)
  const [actioning, setActioning] = useState(null)

  useEffect(() => {
    axios.get('/api/admin/suspension-requests', { headers })
      .then(r => setRequests(r.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers])

  async function handleAction(id, status) {
    const label = status === 'approved' ? 'approve' : 'reject'
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} this suspension request?`)) return
    setActioning(id)
    try {
      await axios.patch(`/api/admin/suspension-requests/${id}`, { status }, { headers })
      setRequests(prev => prev.filter(r => r.id !== id))
      toast(status === 'approved' ? 'Suspension created.' : 'Request rejected.', 'success')
    } catch (e) {
      toast(e.response?.data?.error ?? 'Action failed.', 'error')
    } finally {
      setActioning(null)
    }
  }

  if (loading) return <div className="admin-loading">Loading…</div>
  if (requests.length === 0) return <div className="admin-empty">No pending suspension requests.</div>

  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Target</th>
            <th>Reason</th>
            <th>Duration</th>
            <th>Requested by</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {requests.map(r => (
            <tr key={r.id}>
              <td>
                {r.target_username
                  ? <Link to={`/user/${r.target_username}`} className="admin-link">@{r.target_username}</Link>
                  : r.user_id.slice(0, 8) + '…'}
              </td>
              <td>{r.reason}</td>
              <td>{r.duration}</td>
              <td>
                {r.requester_username
                  ? <Link to={`/user/${r.requester_username}`} className="admin-link">@{r.requester_username}</Link>
                  : '—'}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(r.created_at)}</td>
              <td>
                <div className="admin-action-row">
                  <button
                    className="admin-action-btn admin-action-btn--green"
                    disabled={actioning === r.id}
                    onClick={() => handleAction(r.id, 'approved')}
                  >Approve</button>
                  <button
                    className="admin-action-btn admin-action-btn--red"
                    disabled={actioning === r.id}
                    onClick={() => handleAction(r.id, 'rejected')}
                  >Reject</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Flagged Content tab (admin + mod) ────────────────────────────────────────

function FlaggedContentTab({ headers }) {
  const toast = useToast()
  const [flags, setFlags]     = useState([])
  const [loading, setLoading] = useState(true)
  const [reviewing, setReviewing] = useState(null)
  const [viewingMessage, setViewingMessage] = useState(null)
  const [viewingContent, setViewingContent] = useState(null)
  const [statusFilter, setStatusFilter] = useState('pending')

  const load = useCallback((status) => {
    setLoading(true)
    axios.get(`/api/admin/content-flags?status=${status}`, { headers })
      .then(r => setFlags(r.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers])

  useEffect(() => { load(statusFilter) }, [statusFilter, load])

  async function setReviewed(id, reviewed) {
    setReviewing(id)
    try {
      const body = reviewed ? {} : { reviewed: false }
      await axios.patch(`/api/admin/content-flags/${id}`, body, { headers })
      if (statusFilter === 'all') {
        setFlags(prev => prev.map(f => f.id === id ? { ...f, reviewed } : f))
      } else {
        setFlags(prev => prev.filter(f => f.id !== id))
      }
      toast(reviewed ? 'Flagged content marked as reviewed.' : 'Flag returned to pending.', 'success')
    } catch (e) {
      toast(e.response?.data?.error ?? (reviewed ? 'Failed to mark as reviewed.' : 'Failed to unreview.'), 'error')
    } finally {
      setReviewing(null)
    }
  }

  const FILTERS = ['pending', 'reviewed', 'all']
  const emptyLabel = statusFilter === 'pending' ? 'No unreviewed flagged content.'
    : statusFilter === 'reviewed' ? 'No reviewed flagged content.'
    : 'No flagged content.'

  return (
    <div className="admin-reports">
      <div className="admin-filter-row">
        {FILTERS.map(s => (
          <button key={s} className={`admin-filter-btn${statusFilter === s ? ' admin-filter-btn--active' : ''}`} onClick={() => setStatusFilter(s)}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
      {loading ? <div className="admin-loading">Loading…</div>
        : flags.length === 0 ? <div className="admin-empty">{emptyLabel}</div>
        : (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>User</th>
            <th>Type</th>
            <th>Preview</th>
            <th>Reasons</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {flags.map(f => {
            const href = contentPermalink(f.permalinkType, f.permalinkId, f.contentId)
            const isMessage = f.contentType === 'message'
            // quiz_comment is not a CONTENT_TYPES key on the route; it resolves
            // via comment.flagTypes server-side, so the modal must open as 'comment'.
            const modalType = f.contentType === 'quiz_comment' ? 'comment' : f.contentType
            return (
            <tr key={f.id}>
              <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(f.createdAt)}</td>
              <td>
                {f.username
                  ? <Link to={`/user/${f.username}`} className="admin-link">@{f.username}</Link>
                  : '—'}
              </td>
              <td style={{ textTransform: 'capitalize' }}>{f.contentType.replace(/_/g, ' ')}</td>
              <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                {f.contentPreview || '—'}
              </td>
              <td>
                {f.reasons.map(r => (
                  <span key={r} className="admin-badge" style={{ background: '#ef4444', marginRight: 4 }}>
                    {r.replace(/_/g, ' ')}
                  </span>
                ))}
              </td>
              <td>
                <div className="admin-action-row">
                  {isMessage && (
                    <button
                      className="admin-action-btn admin-action-btn--blue"
                      onClick={() => setViewingMessage(f.contentId)}
                    >View message</button>
                  )}
                  {!isMessage && href && (
                    <Link to={href} target="_blank" rel="noopener noreferrer" className="admin-action-btn admin-action-btn--blue">
                      View {f.contentType.replace(/_/g, ' ')}
                    </Link>
                  )}
                  {VIEWABLE_CONTENT_TYPES.includes(modalType) && (
                    <button
                      className="admin-action-btn admin-action-btn--blue"
                      onClick={() => setViewingContent({ type: modalType, id: f.contentId })}
                    >View</button>
                  )}
                  {f.contentType === 'wall_post' && f.username && (
                    <Link to={`/user/${f.username}?tab=wall&post=${f.contentId}`} target="_blank" rel="noopener noreferrer" className="admin-action-btn admin-action-btn--blue">
                      View on wall
                    </Link>
                  )}
                  {f.contentType === 'wall_comment' && f.wallOwnerUsername && (
                    <Link to={`/user/${f.wallOwnerUsername}?tab=wall&comment=${f.contentId}`} target="_blank" rel="noopener noreferrer" className="admin-action-btn admin-action-btn--blue">
                      View on wall
                    </Link>
                  )}
                  {!f.reviewed && (
                    <button
                      className="admin-action-btn admin-action-btn--green"
                      disabled={reviewing === f.id}
                      onClick={() => setReviewed(f.id, true)}
                    >
                      {reviewing === f.id ? '…' : 'Mark Reviewed'}
                    </button>
                  )}
                  {f.reviewed && (
                    <button
                      className="admin-action-btn admin-action-btn--orange"
                      disabled={reviewing === f.id}
                      onClick={() => setReviewed(f.id, false)}
                    >
                      {reviewing === f.id ? '…' : 'Unreview'}
                    </button>
                  )}
                </div>
                {f.contentType === 'wall_comment' && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', userSelect: 'all', marginTop: 2 }}>ID: {f.contentId}</div>
                )}
              </td>
            </tr>
          )
          })}
        </tbody>
      </table>
    </div>
        )}
      {viewingMessage && (
        <MessageViewerModal
          messageId={viewingMessage}
          headers={headers}
          onClose={() => setViewingMessage(null)}
        />
      )}
      {viewingContent && (
        <ContentViewerModal
          type={viewingContent.type}
          id={viewingContent.id}
          headers={headers}
          onClose={() => setViewingContent(null)}
        />
      )}
    </div>
  )
}

// ── Audit Log tab (admin only) ──────────────────────────────────────────────

function AuditLogTab({ headers }) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [offset, setOffset]   = useState(0)
  const [expandedId, setExpandedId] = useState(null)
  const LIMIT = 50

  useEffect(() => {
    setLoading(true)
    axios.get(`/api/admin/audit-log?limit=${LIMIT}&offset=${offset}`, { headers })
      .then(r => setEntries(r.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers, offset])

  if (loading) return <div className="admin-loading">Loading…</div>
  if (entries.length === 0 && offset === 0) return <div className="admin-empty">No audit log entries.</div>

  return (
    <div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Target</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => {
              const hasDetails = e.details && Object.keys(e.details).length > 0
              const isExpanded = expandedId === e.id
              return (
                <tr key={e.id} onClick={() => hasDetails && setExpandedId(isExpanded ? null : e.id)} style={{ cursor: hasDetails ? 'pointer' : 'default' }}>
                  <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(e.createdAt)}</td>
                  <td>
                    {e.actorUsername
                      ? <Link to={`/user/${e.actorUsername}`} className="admin-link" onClick={ev => ev.stopPropagation()}>@{e.actorUsername}</Link>
                      : e.actorId?.slice(0, 8) + '…'}
                  </td>
                  <td>{e.action.replace(/_/g, ' ')}</td>
                  <td style={{ textTransform: 'capitalize' }}>
                    {e.targetType} #{e.targetId?.length > 8 ? e.targetId.slice(0, 8) + '…' : e.targetId}
                  </td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)', maxWidth: 300 }}>
                    {!hasDetails ? '—' : isExpanded
                      ? <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.75rem', background: 'var(--surface)', padding: 8, borderRadius: 6 }}>{JSON.stringify(e.details, null, 2)}</pre>
                      : <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{JSON.stringify(e.details)}</span>
                    }
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
        <button className="admin-action-btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
          ← Previous
        </button>
        <button className="admin-action-btn" disabled={entries.length < LIMIT} onClick={() => setOffset(offset + LIMIT)}>
          Next →
        </button>
      </div>
    </div>
  )
}

// ── Ban Evasion tab (admin + mod) ────────────────────────────────────────────

function BanEvasionTab({ headers }) {
  const toast = useToast()
  const [flags, setFlags]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [statusFilter, setStatusFilter] = useState('pending')
  const [actioning, setActioning]   = useState(null)
  const [ipModal, setIpModal]       = useState(null) // { userId, username }
  const [ipData, setIpData]         = useState(null)
  const [ipLoading, setIpLoading]   = useState(false)
  const [ipAccounts, setIpAccounts] = useState(null) // { ip, accounts[] }

  const load = useCallback((status) => {
    setLoading(true)
    axios.get(`/api/admin/evasion-flags?status=${status}`, { headers })
      .then(r => setFlags(r.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers])

  useEffect(() => { load(statusFilter) }, [statusFilter, load])

  async function updateFlag(id, status, notes) {
    setActioning(id)
    try {
      await axios.patch(`/api/admin/evasion-flags/${id}`, { status, notes }, { headers })
      setFlags(prev => prev.filter(f => f.id !== id))
      toast(status === 'confirmed' ? 'Flag confirmed.' : 'Flag dismissed.', 'success')
    } catch (e) {
      toast(e.response?.data?.error ?? 'Action failed.', 'error')
    } finally {
      setActioning(null)
    }
  }

  async function confirmAndSuspend(flag) {
    if (!window.confirm(`Confirm evasion and suspend @${flag.flaggedUsername}?`)) return
    setActioning(flag.id)
    try {
      await axios.post(
        `/api/admin/evasion-flags/${flag.id}/confirm-and-suspend`,
        {
          reason:   `Ban evasion (matched IP with @${flag.matchedUsername})`,
          duration: '30d',
        },
        { headers }
      )
      setFlags(prev => prev.filter(f => f.id !== flag.id))
      toast(`@${flag.flaggedUsername} suspended for 30 days.`, 'success')
    } catch (e) {
      toast(e.response?.data?.error ?? 'Action failed.', 'error')
    } finally {
      setActioning(null)
    }
  }

  async function openIpHistory(userId, username) {
    setIpModal({ userId, username })
    setIpData(null)
    setIpAccounts(null)
    setIpLoading(true)
    try {
      const r = await axios.get(`/api/admin/users/${userId}/ip-history`, { headers })
      setIpData(r.data)
    } catch (e) {
      console.error(e)
    } finally {
      setIpLoading(false)
    }
  }

  async function showIpAccounts(ip) {
    setIpAccounts({ ip, accounts: null })
    try {
      const r = await axios.get(`/api/admin/ip/${encodeURIComponent(ip)}/accounts`, { headers })
      setIpAccounts({ ip, accounts: r.data })
    } catch (e) {
      console.error(e)
    }
  }

  const CONFIDENCE_COLORS = { high: '#DC2626', medium: '#F59E0B', low: '#EAB308' }
  const FILTERS = ['pending', 'confirmed', 'dismissed', 'all']

  return (
    <div className="admin-reports">
      <div className="admin-filter-row">
        {FILTERS.map(s => (
          <button key={s} className={`admin-filter-btn${statusFilter === s ? ' admin-filter-btn--active' : ''}`} onClick={() => setStatusFilter(s)}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <div className="admin-loading">Loading…</div>
        : flags.length === 0 ? <div className="admin-empty">No {statusFilter === 'all' ? '' : statusFilter} evasion flags.</div>
        : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Flagged Account</th>
                  <th>Matched (Suspended)</th>
                  <th>Shared IP</th>
                  <th>Confidence</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {flags.map(f => (
                  <tr key={f.id}>
                    <td>
                      {f.flaggedUsername
                        ? <Link to={`/user/${f.flaggedUsername}`} className="admin-link">@{f.flaggedUsername}</Link>
                        : f.flaggedUserId.slice(0, 8) + '…'}
                    </td>
                    <td>
                      {f.matchedUsername
                        ? <Link to={`/user/${f.matchedUsername}`} className="admin-link">@{f.matchedUsername}</Link>
                        : f.matchedUserId.slice(0, 8) + '…'}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{f.ipAddress}</td>
                    <td>
                      <span className="admin-badge" style={{ background: CONFIDENCE_COLORS[f.confidence] ?? '#6b7280' }}>
                        {f.confidence}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(f.createdAt)}</td>
                    <td>
                      <div className="admin-action-row">
                        {f.status === 'pending' && (
                          <>
                            <button className="admin-action-btn admin-action-btn--red" disabled={actioning === f.id} onClick={() => confirmAndSuspend(f)}>
                              Confirm & Suspend
                            </button>
                            <button className="admin-action-btn admin-action-btn--gray" disabled={actioning === f.id} onClick={() => {
                              const note = window.prompt('Dismiss reason (optional):')
                              updateFlag(f.id, 'dismissed', note || null)
                            }}>
                              Dismiss
                            </button>
                          </>
                        )}
                        <button className="admin-action-btn admin-action-btn--blue" onClick={() => openIpHistory(f.flaggedUserId, f.flaggedUsername)}>
                          IP History
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* IP History Modal */}
      {ipModal && (
        <div className="modal-overlay" onClick={() => { setIpModal(null); setIpAccounts(null) }}>
          <div className="tip-modal" style={{ maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>IP History — @{ipModal.username}</h3>
              <button onClick={() => { setIpModal(null); setIpAccounts(null) }} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text)' }}>×</button>
            </div>

            {ipLoading ? <div className="admin-loading">Loading…</div> : ipData ? (
              <>
                <table className="admin-table" style={{ fontSize: '0.8rem' }}>
                  <thead>
                    <tr><th>IP Address</th><th>Action</th><th>Other Accounts</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {ipData.logs.map(log => (
                      <tr key={log.id}>
                        <td>
                          <button className="admin-link" style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.8rem', padding: 0, color: 'var(--accent)' }} onClick={() => showIpAccounts(log.ipAddress)}>
                            {log.ipAddress}
                          </button>
                        </td>
                        <td>{log.action}</td>
                        <td>{ipData.ipAccountCounts[log.ipAddress] ?? 0}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(log.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {ipAccounts && (
                  <div style={{ marginTop: 16, padding: 12, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '0.85rem' }}>Accounts using {ipAccounts.ip}:</div>
                    {ipAccounts.accounts === null ? <div>Loading…</div> : ipAccounts.accounts.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No other accounts.</div> : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {ipAccounts.accounts.map(a => (
                          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                            <Link to={`/user/${a.username}`} className="admin-link">@{a.username}</Link>
                            {a.restricted && <span style={{ color: '#f59e0b', fontSize: '0.7rem', fontWeight: 600 }}>RESTRICTED</span>}
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginLeft: 'auto' }}>Joined {timeAgo(a.createdAt)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : <div className="admin-empty">No IP history found.</div>}
          </div>
        </div>
      )}
    </div>
  )
}

// ── CSAM Flags tab (admin only) ─────────────────────────────────────────────

function CsamFlagsTab({ headers }) {
  const toast = useToast()
  const [flags, setFlags]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [statusFilter, setStatusFilter] = useState('pending')
  const [actioning, setActioning]   = useState(null)

  const load = useCallback((status) => {
    setLoading(true)
    axios.get(`/api/admin/csam-flags?status=${status}`, { headers })
      .then(r => setFlags(r.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers])

  useEffect(() => { load(statusFilter) }, [statusFilter, load])

  async function updateFlag(id, status, notes) {
    setActioning(id)
    try {
      await axios.patch(`/api/admin/csam-flags/${id}`, { status, notes }, { headers })
      if (statusFilter === 'all') {
        setFlags(prev => prev.map(f => f.id === id ? { ...f, status } : f))
      } else {
        setFlags(prev => prev.filter(f => f.id !== id))
      }
      if (status === 'false_positive') toast('Marked as false positive — user suspension lifted.', 'success')
      else toast(`Flag marked as ${status}.`, 'success')
    } catch (e) {
      toast(e.response?.data?.error ?? 'Action failed.', 'error')
    } finally {
      setActioning(null)
    }
  }

  const FILTERS = ['pending', 'reviewed', 'escalated', 'false_positive', 'all']
  const CONFIDENCE_COLORS = { high: '#DC2626', medium: '#F59E0B', low: '#EAB308' }

  return (
    <div className="admin-reports">
      <div className="admin-filter-row">
        {FILTERS.map(s => (
          <button key={s} className={`admin-filter-btn${statusFilter === s ? ' admin-filter-btn--active' : ''}`} onClick={() => setStatusFilter(s)}>
            {s === 'false_positive' ? 'False Positive' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <div className="admin-loading">Loading…</div>
        : flags.length === 0 ? <div className="admin-empty">No {statusFilter === 'all' ? '' : statusFilter} CSAM flags.</div>
        : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Image Type</th>
                  <th>Hash Match</th>
                  <th>Distance</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {flags.map(f => (
                  <tr key={f.id}>
                    <td>
                      {f.username
                        ? <Link to={`/user/${f.username}`} className="admin-link">@{f.username}</Link>
                        : f.userId?.slice(0, 8) + '…'}
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      Flagged image — not displayed for safety.
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{f.matchedHash?.slice(0, 12)}…</td>
                    <td>
                      <span className="admin-badge" style={{ background: CONFIDENCE_COLORS[f.hammingDistance <= 2 ? 'high' : f.hammingDistance <= 4 ? 'medium' : 'low'] }}>
                        {f.hammingDistance}/64
                      </span>
                    </td>
                    <td>
                      <span className="admin-badge" style={{ background: f.status === 'pending' ? '#f59e0b' : f.status === 'escalated' ? '#DC2626' : '#22c55e' }}>
                        {f.status}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(f.createdAt)}</td>
                    <td>
                      <div className="admin-action-row">
                        {f.status === 'pending' && (
                          <>
                            <button className="admin-action-btn admin-action-btn--green" disabled={actioning === f.id} onClick={() => updateFlag(f.id, 'reviewed')}>
                              Mark Reviewed
                            </button>
                            <button className="admin-action-btn admin-action-btn--red" disabled={actioning === f.id} onClick={() => updateFlag(f.id, 'escalated')}>
                              Escalate
                            </button>
                            <button className="admin-action-btn admin-action-btn--orange" disabled={actioning === f.id} onClick={() => {
                              if (window.confirm('Mark as false positive? This will un-suspend the flagged user.')) {
                                const note = window.prompt('Note (optional):')
                                updateFlag(f.id, 'false_positive', note || null)
                              }
                            }}>
                              False Positive
                            </button>
                          </>
                        )}
                        {(f.status === 'pending' || f.status === 'escalated') && f.userId && (
                          <button className="admin-action-btn admin-action-btn--red" disabled={actioning === f.id} onClick={async () => {
                            if (!window.confirm(`Freeze @${f.username ?? 'this user'}? This preserves all content and blocks account deletion.`)) return
                            setActioning(f.id)
                            try {
                              await axios.post(`/api/admin/csam-freeze/${f.userId}`, { csamFlagId: f.id }, { headers })
                              toast('Account frozen.', 'success')
                            } catch (e) {
                              toast(e.response?.data?.error ?? 'Freeze failed.', 'error')
                            } finally { setActioning(null) }
                          }}>
                            CSAM Freeze
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  )
}

// ── CSAM Frozen Accounts tab (admin only) ──────────────────────────────────

function CsamFrozenTab({ headers }) {
  const toast = useToast()
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading]   = useState(true)
  const [actioning, setActioning] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    axios.get('/api/admin/csam-frozen', { headers })
      .then(r => setAccounts(r.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers])

  useEffect(() => { load() }, [load])

  async function unfreeze(userId, username) {
    if (!window.confirm(`Unfreeze @${username ?? 'this user'}? They will regain full account access.`)) return
    setActioning(userId)
    try {
      await axios.delete(`/api/admin/csam-freeze/${userId}`, { headers })
      setAccounts(prev => prev.filter(a => a.id !== userId))
      toast('Account unfrozen.', 'success')
    } catch (e) {
      toast(e.response?.data?.error ?? 'Unfreeze failed.', 'error')
    } finally { setActioning(null) }
  }

  return (
    <div className="admin-reports">
      {loading ? <div className="admin-loading">Loading…</div>
        : accounts.length === 0 ? <div className="admin-empty">No frozen accounts.</div>
        : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Frozen</th>
                  <th>Frozen By</th>
                  <th>Flags</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(a => (
                  <tr key={a.id}>
                    <td>
                      {a.username
                        ? <Link to={`/user/${a.username}`} className="admin-link">@{a.username}</Link>
                        : a.id.slice(0, 8) + '…'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{a.frozenAt ? timeAgo(a.frozenAt) : '—'}</td>
                    <td>{a.frozenBy ? `@${a.frozenBy}` : '—'}</td>
                    <td>
                      <span className="admin-badge" style={{ background: a.flagCount > 0 ? '#DC2626' : '#6B7280' }}>
                        {a.flagCount}
                      </span>
                    </td>
                    <td>
                      <button className="admin-action-btn admin-action-btn--green" disabled={actioning === a.id} onClick={() => unfreeze(a.id, a.username)}>
                        Unfreeze
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  )
}

// ── CSAM Blocklist tab (admin only) ─────────────────────────────────────────

function CsamBlocklistTab({ headers }) {
  const toast = useToast()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding]   = useState(false)
  const [form, setForm]       = useState({ hash_value: '', hash_type: 'phash', source: '' })

  useEffect(() => {
    axios.get('/api/admin/csam-blocklist', { headers })
      .then(r => setEntries(r.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers])

  async function handleAdd(e) {
    e.preventDefault()
    if (!form.hash_value.trim() || !form.source.trim()) return
    setAdding(true)
    try {
      const { data } = await axios.post('/api/admin/csam-blocklist', form, { headers })
      setEntries(prev => [{ ...data, hashValue: data.hash_value, hashType: data.hash_type, addedBy: null, createdAt: data.created_at }, ...prev])
      setForm({ hash_value: '', hash_type: 'phash', source: '' })
      toast('Hash added to blocklist.', 'success')
    } catch (e) {
      toast(e.response?.data?.error ?? 'Failed to add hash.', 'error')
    } finally {
      setAdding(false)
    }
  }

  if (loading) return <div className="admin-loading">Loading…</div>

  return (
    <div>
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          className="admin-search"
          placeholder="Hash value"
          value={form.hash_value}
          onChange={e => setForm(f => ({ ...f, hash_value: e.target.value }))}
          style={{ flex: 2, minWidth: 200 }}
        />
        <select
          className="admin-search"
          value={form.hash_type}
          onChange={e => setForm(f => ({ ...f, hash_type: e.target.value }))}
          style={{ flex: 1, minWidth: 100 }}
        >
          <option value="phash">pHash</option>
          <option value="photodna">PhotoDNA</option>
          <option value="md5">MD5</option>
        </select>
        <input
          className="admin-search"
          placeholder="Source (e.g. manual, ncmec)"
          value={form.source}
          onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
          style={{ flex: 1, minWidth: 120 }}
        />
        <button type="submit" className="admin-action-btn admin-action-btn--green" disabled={adding || !form.hash_value.trim() || !form.source.trim()}>
          {adding ? '…' : 'Add'}
        </button>
      </form>

      {entries.length === 0 ? <div className="admin-empty">No hashes in blocklist.</div> : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Hash</th>
                <th>Type</th>
                <th>Source</th>
                <th>Added by</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{e.hashValue}</td>
                  <td>{e.hashType}</td>
                  <td>{e.source}</td>
                  <td>{e.addedBy ? <Link to={`/user/${e.addedBy}`} className="admin-link">@{e.addedBy}</Link> : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(e.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Wall Posts tab (admin only) ─────────────────────────────────────────────

function WallPostsTab({ headers, filterId }) {
  const toast = useToast()
  const [posts, setPosts]     = useState([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal]     = useState(0)
  const [offset, setOffset]   = useState(0)
  const [actioning, setActioning] = useState(null)
  const [expandedPosts, setExpandedPosts] = useState(() => new Set())
  const LIMIT = 50

  function togglePost(id) {
    setExpandedPosts(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const load = useCallback((off) => {
    setLoading(true)
    const idParam = filterId ? `&id=${encodeURIComponent(filterId)}` : ''
    axios.get(`/api/admin/wall-posts?limit=${LIMIT}&offset=${off}${idParam}`, { headers })
      .then(r => { setPosts(r.data.posts); setTotal(r.data.total) })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers, filterId])

  useEffect(() => { load(offset) }, [offset, load])

  async function toggleHide(post) {
    setActioning(post.id)
    try {
      await axios.patch(`/api/admin/hide/wall-post/${post.id}`, { hidden: !post.hidden }, { headers })
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, hidden: !p.hidden } : p))
      toast(post.hidden ? 'Post unhidden.' : 'Post hidden.', 'success')
    } catch (e) { toast(e.response?.data?.error ?? 'Failed.', 'error') }
    finally { setActioning(null) }
  }

  if (loading) return <div className="admin-loading">Loading...</div>

  return (
    <div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Author</th>
              <th>Content</th>
              <th>Likes</th>
              <th>Comments</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center' }}>No wall posts found.</td></tr>}
            {posts.map(p => (
              <tr key={p.id} style={{ opacity: p.deleted ? 0.5 : 1 }}>
                <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(p.created_at)}</td>
                <td>
                  {p.profile
                    ? <Link to={`/user/${p.profile.username}`} className="admin-link">@{p.profile.username}</Link>
                    : '—'}
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', userSelect: 'all', marginTop: 2 }}>ID: {p.id}</div>
                </td>
                {p.content
                  ? <td
                      className={`admin-table__details admin-table__details--clickable${expandedPosts.has(p.id) ? ' admin-table__details--expanded' : ''}`}
                      style={{ fontSize: '0.85rem' }}
                      role="button"
                      tabIndex={0}
                      aria-expanded={expandedPosts.has(p.id)}
                      title={expandedPosts.has(p.id) ? 'Click to collapse' : 'Click to read full content'}
                      onClick={() => togglePost(p.id)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePost(p.id) } }}
                    >{p.content}</td>
                  : <td className="admin-table__details" style={{ fontSize: '0.85rem' }}>—</td>}
                <td>{p.likes_count}</td>
                <td>{p.comments_count}</td>
                <td>
                  {p.hidden && <span className="admin-badge" style={{ background: '#ef4444', marginRight: 4 }}>Hidden</span>}
                  {p.deleted && <span className="admin-badge" style={{ background: '#6b7280' }}>Deleted</span>}
                  {!p.hidden && !p.deleted && <span className="admin-badge" style={{ background: '#22c55e' }}>Visible</span>}
                </td>
                <td style={{ display: 'flex', gap: 4 }}>
                  <button className={`admin-action-btn ${p.hidden ? 'admin-action-btn--green' : 'admin-action-btn--orange'}`} disabled={actioning === p.id} onClick={() => toggleHide(p)}>
                    {p.hidden ? 'Unhide' : 'Hide'}
                  </button>
                  {p.profile && <Link to={`/user/${p.profile.username}?tab=wall&post=${p.id}`} className="admin-action-btn admin-action-btn--blue">View Wall</Link>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > LIMIT && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
          <button className="admin-action-btn" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}>Prev</button>
          <span style={{ padding: '6px 0', fontSize: '0.85rem' }}>{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
          <button className="admin-action-btn" disabled={offset + LIMIT >= total} onClick={() => setOffset(o => o + LIMIT)}>Next</button>
        </div>
      )}
    </div>
  )
}

// ── Wall Comments tab (admin only) ──────────────────────────────────────────

function WallCommentsTab({ headers, filterId }) {
  const toast = useToast()
  const [comments, setComments] = useState([])
  const [loading, setLoading]   = useState(true)
  const [total, setTotal]       = useState(0)
  const [offset, setOffset]     = useState(0)
  const [actioning, setActioning] = useState(null)
  const [expandedComments, setExpandedComments] = useState(() => new Set())
  const LIMIT = 50

  function toggleComment(id) {
    setExpandedComments(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const load = useCallback((off) => {
    setLoading(true)
    const idParam = filterId ? `&id=${encodeURIComponent(filterId)}` : ''
    axios.get(`/api/admin/wall-comments?limit=${LIMIT}&offset=${off}${idParam}`, { headers })
      .then(r => { setComments(r.data.comments); setTotal(r.data.total) })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers, filterId])

  useEffect(() => { load(offset) }, [offset, load])

  async function toggleHide(c) {
    setActioning(c.id)
    try {
      await axios.patch(`/api/admin/hide/wall-comment/${c.id}`, { hidden: !c.hidden }, { headers })
      setComments(prev => prev.map(x => x.id === c.id ? { ...x, hidden: !c.hidden } : x))
      toast(c.hidden ? 'Comment unhidden.' : 'Comment hidden.', 'success')
    } catch (e) { toast(e.response?.data?.error ?? 'Failed.', 'error') }
    finally { setActioning(null) }
  }

  if (loading) return <div className="admin-loading">Loading...</div>

  return (
    <div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Author</th>
              <th>Content</th>
              <th>Post ID</th>
              <th>Likes</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {comments.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center' }}>No wall comments found.</td></tr>}
            {comments.map(c => (
              <tr key={c.id} style={{ opacity: c.deleted ? 0.5 : 1 }}>
                <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(c.created_at)}</td>
                <td>
                  {c.profile
                    ? <Link to={`/user/${c.profile.username}`} className="admin-link">@{c.profile.username}</Link>
                    : '—'}
                </td>
                {c.content
                  ? <td
                      className={`admin-table__details admin-table__details--clickable${expandedComments.has(c.id) ? ' admin-table__details--expanded' : ''}`}
                      style={{ fontSize: '0.85rem' }}
                      role="button"
                      tabIndex={0}
                      aria-expanded={expandedComments.has(c.id)}
                      title={expandedComments.has(c.id) ? 'Click to collapse' : 'Click to read full content'}
                      onClick={() => toggleComment(c.id)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleComment(c.id) } }}
                    >{c.content}</td>
                  : <td className="admin-table__details" style={{ fontSize: '0.85rem' }}>—</td>}
                <td style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                  {c.post_id?.slice(0, 8)}
                  <div style={{ color: 'var(--text-muted)', userSelect: 'all', marginTop: 2 }}>ID: {c.id}</div>
                </td>
                <td>{c.likes_count}</td>
                <td>
                  {c.hidden && <span className="admin-badge" style={{ background: '#ef4444', marginRight: 4 }}>Hidden</span>}
                  {c.deleted && <span className="admin-badge" style={{ background: '#6b7280' }}>Deleted</span>}
                  {!c.hidden && !c.deleted && <span className="admin-badge" style={{ background: '#22c55e' }}>Visible</span>}
                </td>
                <td style={{ display: 'flex', gap: 4 }}>
                  <button className={`admin-action-btn ${c.hidden ? 'admin-action-btn--green' : 'admin-action-btn--orange'}`} disabled={actioning === c.id} onClick={() => toggleHide(c)}>
                    {c.hidden ? 'Unhide' : 'Hide'}
                  </button>
                  {c.wallOwnerUsername && <Link to={`/user/${c.wallOwnerUsername}?tab=wall&comment=${c.id}`} className="admin-action-btn admin-action-btn--blue">View Wall</Link>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > LIMIT && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
          <button className="admin-action-btn" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}>Prev</button>
          <span style={{ padding: '6px 0', fontSize: '0.85rem' }}>{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
          <button className="admin-action-btn" disabled={offset + LIMIT >= total} onClick={() => setOffset(o => o + LIMIT)}>Next</button>
        </div>
      )}
    </div>
  )
}

// ── Moderation Queue tab ────────────────────────────────────────────────────

function ModerationQueueTab({ headers }) {
  const toast = useToast()
  const [items, setItems]       = useState([])
  const [loading, setLoading]   = useState(true)
  const [total, setTotal]       = useState(0)
  const [actioning, setActioning] = useState(null)
  const [statusFilter, setStatusFilter] = useState('unreviewed')

  const load = useCallback((status) => {
    setLoading(true)
    axios.get(`/api/admin/moderation-queue?status=${status}&limit=50`, { headers })
      .then(r => { setItems(r.data.items); setTotal(r.data.total) })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [headers])

  useEffect(() => { load(statusFilter) }, [statusFilter, load])

  async function markReviewed(id) {
    setActioning(id)
    try {
      await axios.patch(`/api/admin/moderation-queue/${id}`, { action_taken: 'reviewed' }, { headers })
      setItems(prev => prev.filter(i => i.id !== id))
      setTotal(t => t - 1)
      toast('Marked as reviewed.', 'success')
    } catch (e) { toast(e.response?.data?.error ?? 'Failed.', 'error') }
    finally { setActioning(null) }
  }

  if (loading) return <div className="admin-loading">Loading...</div>

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {['unreviewed', 'reviewed', 'all'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`admin-action-btn ${statusFilter === s ? 'admin-action-btn--blue' : ''}`}
            style={{ textTransform: 'capitalize' }}>{s}</button>
        ))}
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Auto-hidden</th>
              <th>Type</th>
              <th>Offender</th>
              <th>Content ID</th>
              <th>Reports</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center' }}>No items.</td></tr>}
            {items.map(i => (
              <tr key={i.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{timeAgo(i.auto_hidden_at)}</td>
                <td style={{ textTransform: 'capitalize' }}>{(i.content_type ?? '').replace(/_/g, ' ')}</td>
                <td>{i.offender_username ? '@' + i.offender_username : (i.offender_user_id ? i.offender_user_id.slice(0, 8) : '—')}</td>
                <td style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{i.content_id?.slice(0, 8)}</td>
                <td>{i.report_count}</td>
                <td>
                  {i.reviewed_at
                    ? <span className="admin-badge" style={{ background: '#22c55e' }}>{i.action_taken ?? 'Reviewed'}</span>
                    : <span className="admin-badge" style={{ background: '#f59e0b' }}>Pending</span>}
                </td>
                <td>
                  {!i.reviewed_at && (
                    <button className="admin-action-btn admin-action-btn--green" disabled={actioning === i.id} onClick={() => markReviewed(i.id)}>
                      {actioning === i.id ? '...' : 'Mark Reviewed'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminPage() {
  const { getToken, profile } = useAuth()
  const navigate     = useNavigate()
  const [searchParams] = useSearchParams()
  const [tab, setTab]         = useState(() => searchParams.get('tab') || 'overview')
  const filterId = searchParams.get('id') || null
  const [headers, setHeaders] = useState(null)
  const [forbidden, setForbidden] = useState(false)
  // Child-safety tab group starts collapsed on every mount (intentionally not
  // persisted) so a logged CSAM surface is never reachable by accidental
  // keyboard/arrow navigation — only after a deliberate expand.
  const [csamGroupOpen, setCsamGroupOpen] = useState(false)

  const userRole = profile?.isAdmin ? 'admin' : profile?.isModerator ? 'moderator' : null

  useEffect(() => {
    getToken().then(token => {
      // ProtectedRoute (App.jsx:127) gates anon at route mount, so reaching
      // here with no token means a previously-authenticated session went
      // away between mount and this effect (signed out in another tab,
      // token revoked, etc.). Use session_expired, not login_required.
      if (!token) { navigate('/login?reason=session_expired'); return }
      const h = { Authorization: `Bearer ${token}` }
      // Verify admin/mod access with a quick probe
      axios.get('/api/admin/stats', { headers: h })
        .then(() => setHeaders(h))
        .catch(() => setForbidden(true))
    })
  }, [])

  if (forbidden) {
    return (
      <div className="legal-page">
        <Navbar />
        <div className="admin-forbidden">
          <div className="admin-forbidden__code">403</div>
          <div className="admin-forbidden__msg">You don't have permission to access this page.</div>
        </div>
      </div>
    )
  }

  if (!headers) return null

  const TABS = [
    { id: 'overview',  label: 'Overview' },
    { id: 'reports',   label: 'Reports' },
    { id: 'users',     label: 'Users' },
    { id: 'flags',     label: 'Flagged Content' },
    // Ban Evasion exposes raw IPs + cross-account IP correlation — admin-only,
    // gated the same way the admin-only tabs below and the CSAM tabs are. A
    // moderator session never renders this tab; the four backing routes
    // (/evasion-flags, /users/:id/ip-history, /ip/:ip/accounts) are
    // requireAdmin+adminOnly server-side.
    ...(userRole === 'admin' ? [
      { id: 'evasion',   label: 'Ban Evasion' },
    ] : []),
    { id: 'wall-posts',    label: 'Wall Posts' },
    { id: 'wall-comments', label: 'Wall Comments' },
    { id: 'mod-queue',     label: 'Mod Queue' },
    ...(userRole === 'admin' ? [
      { id: 'audit',         label: 'Audit Log' },
      { id: 'requests',      label: 'Suspension Requests' },
      { id: 'sysmsg',        label: 'System Messages' },
    ] : []),
  ]

  // Child-safety tabs are deliberately kept OUT of the normal tab-through /
  // arrow-scroll order. They live behind a collapsed "Child safety" group and
  // are only rendered as focusable tab buttons once the admin expands it, so
  // accidental keyboard navigation can't land on a logged CSAM surface.
  // Admin-only; the panel render blocks below are unchanged.
  const CSAM_TABS = userRole === 'admin' ? [
    { id: 'csam-flags',  label: 'CSAM Flags' },
    { id: 'csam-frozen', label: 'CSAM Frozen' },
    { id: 'blocklist',   label: 'Hash Blocklist' },
  ] : []

  return (
    <div className="legal-page">
      <Navbar />
      <div className="admin-page">
        <div className="admin-header">
          <h1 className="admin-header__title">Admin</h1>
          <div className="admin-tabs" role="tablist" aria-label="Admin sections">
            {TABS.map(t => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                className={`admin-tab${tab === t.id ? ' admin-tab--active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
            {CSAM_TABS.length > 0 && (
              <button
                type="button"
                className={`admin-tab admin-tab--group-toggle${csamGroupOpen ? ' admin-tab--active' : ''}`}
                aria-expanded={csamGroupOpen}
                aria-controls="admin-csam-panel"
                onClick={() => setCsamGroupOpen(o => !o)}
              >
                Child safety {csamGroupOpen ? '▾' : '▸'}
              </button>
            )}
          </div>

          {/* Child-safety panel. Rendered ONLY when expanded, so the three CSAM
              tab buttons are absent from the DOM and the keyboard focus/tab order
              while collapsed (not merely visually hidden). Lives below the tab
              row rather than inline, so it is never clipped by the tab bar's
              horizontal overflow scroll. */}
          {csamGroupOpen && CSAM_TABS.length > 0 && (
            <div className="admin-csam-panel" id="admin-csam-panel" role="group" aria-label="Child safety">
              {CSAM_TABS.map(t => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={tab === t.id}
                  className={`admin-tab${tab === t.id ? ' admin-tab--active' : ''}`}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="admin-content" role="tabpanel" aria-label={tab}>
          {tab === 'overview'  && <OverviewTab  headers={headers} />}
          {tab === 'reports'   && <ReportsTab   headers={headers} userRole={userRole} />}
          {tab === 'users'     && <UsersTab     headers={headers} userRole={userRole} />}
          {tab === 'flags'     && <FlaggedContentTab headers={headers} />}
          {tab === 'evasion'   && <BanEvasionTab headers={headers} />}
          {tab === 'wall-posts'    && <WallPostsTab headers={headers} filterId={filterId} />}
          {tab === 'wall-comments' && <WallCommentsTab headers={headers} filterId={filterId} />}
          {tab === 'mod-queue'     && <ModerationQueueTab headers={headers} />}
          {tab === 'audit'       && <AuditLogTab headers={headers} />}
          {tab === 'csam-flags'  && <CsamFlagsTab headers={headers} />}
          {tab === 'csam-frozen' && <CsamFrozenTab headers={headers} />}
          {tab === 'blocklist'   && <CsamBlocklistTab headers={headers} />}
          {tab === 'requests'  && <SuspensionRequestsTab headers={headers} />}
          {tab === 'sysmsg'    && <SystemMessagesTab headers={headers} />}
        </div>
      </div>
    </div>
  )
}
